import { useEffect } from 'react';
import { IconButton, ProgressBar } from '@launchpad-ui/components';
import cx from 'clsx';

import { CounterHeader } from '#components/ui/counterHeader';
import { PaginationClick, trackPaginationEvent } from '#utils/paginationUtils';

import styles from './AgnosticPaginationControl.module.css';

// Pagination control that is agnostic of backend pagination
// will be pluralized based on count
const PaginationResourceLabels = {
  aiConfigs: 'AI config',
  aiEvaluations: 'AI evaluation',
  aiModelConfigs: 'AI model config',
  applications: 'application',
  applicationVersions: 'version',
  approvals: 'approval',
  environments: 'environment',
  experiments: 'experiment',
  holdouts: 'holdout',
  flags: 'flag',
  members: 'member',
  payloadFilters: 'payload filter',
  projects: 'project',
  releasePipelines: 'release pipeline',
  segments: 'segment',
  teams: 'team',
  userFlags: 'flag',
  metricsAndGroups: 'metrics and metric group',
  lastSeenMetricEvents: 'last seen metric event',
  monitoringIssues: 'issue',
  monitoringErrors: 'error',
  monitoringIssueBreadcrumbs: 'breadcrumb',
  roles: 'role',
  agentGraphs: 'agent graph',
  agentOptimizations: 'optimization',
};

type PaginationResource = keyof typeof PaginationResourceLabels;

export type PaginationControlProps = {
  currentPageSize: number;
  isReady: boolean;
  limit: number;
  offset: number;
  onPageClick: (actionHint: PaginationClick) => void;
  resourceKind: PaginationResource;
  totalCount?: number;
  className?: string;
  hasInfinitePagination?: boolean;
  resourceLabel?: string;
};

function PaginationControl({
  currentPageSize,
  offset,
  isReady,
  limit,
  onPageClick,
  resourceKind,
  totalCount,
  className,
  hasInfinitePagination = false,
  resourceLabel,
}: PaginationControlProps) {
  const hasNoResults = hasInfinitePagination ? !currentPageSize : !totalCount;
  const nextPageOffset = offset + limit;
  const previousPageOffset = Math.max(offset - limit, 0);
  const hasPreviousPage = previousPageOffset >= 0 && offset > 0;
  const hasNextPage = hasInfinitePagination ? currentPageSize >= limit : nextPageOffset < (totalCount ?? 0);
  const label = resourceLabel || PaginationResourceLabels[resourceKind];
  const renderPaginationText = () => {
    const from = offset + 1;
    const to = !hasInfinitePagination
      ? totalCount && Math.min(offset + currentPageSize, totalCount)
      : offset + currentPageSize || 0;
    if (!isReady) {
      return <ProgressBar aria-label="Loading…" isIndeterminate />;
    }

    if (hasNoResults) {
      return <strong data-test-id="pagination-no-results">No results</strong>;
    }

    return (
      <div className={styles.text}>
        <strong>
          {from}-{to}
        </strong>
        {!hasInfinitePagination && (
          <>
            {' '}
            of <strong>{totalCount}</strong>
          </>
        )}
      </div>
    );
  };

  return (
    <div className={cx(styles.root, className)}>
      <div className={styles.body}>
        <IconButton
          isDisabled={!hasPreviousPage}
          className={hasPreviousPage ? styles.button : styles.disabledButton}
          onPress={() => onPageClick(PaginationClick.FIRST)}
          variant="minimal"
          size="small"
          icon="chevrons-left"
          aria-label={`first ${label} page`}
        />
        <IconButton
          isDisabled={!hasPreviousPage}
          className={hasPreviousPage ? styles.button : styles.disabledButton}
          onPress={() => onPageClick(PaginationClick.PREV)}
          variant="minimal"
          size="small"
          icon="chevron-left"
          aria-label={`previous ${label} page`}
        />
        <div className={styles.hud}>{renderPaginationText()}</div>
        <IconButton
          isDisabled={!hasNextPage}
          className={hasNextPage ? styles.button : styles.disabledButton}
          onPress={() => onPageClick(PaginationClick.NEXT)}
          variant="minimal"
          size="small"
          icon="chevron-right"
          aria-label={`next ${label} page`}
        />
        <IconButton
          isDisabled={!hasNextPage || hasInfinitePagination}
          className={hasNextPage || !hasInfinitePagination ? styles.button : styles.disabledButton}
          onPress={() => onPageClick(PaginationClick.LAST)}
          variant="minimal"
          size="small"
          icon="chevrons-right"
          aria-label={`last ${label} page`}
        />
      </div>
    </div>
  );
}

export type PaginationControlWrapperProps = Omit<PaginationControlProps, 'onPageClick'> & {
  children?: React.ReactNode;
  prevPageOffset?: number;
  nextPageOffset?: number;
  lastPageOffset?: number;
  onPageClick: (actionHint: PaginationClick, newOffset: number) => void;
  limit: number;
  disableAutomaticBottomPagination?: boolean;
  disableAutomaticTopPagination?: boolean;
};

export function PaginationControlWrapper({
  children,
  currentPageSize,
  isReady,
  prevPageOffset,
  nextPageOffset,
  lastPageOffset,
  limit,
  offset,
  onPageClick,
  resourceKind,
  totalCount,
  className,
  disableAutomaticBottomPagination,
  disableAutomaticTopPagination = false,
  hasInfinitePagination = false,
  resourceLabel,
}: PaginationControlWrapperProps) {
  useEffect(() => {
    trackPaginationEvent('view');
  }, []);

  const itemCountWithinLimit = totalCount && totalCount <= limit;
  const shouldRenderBottomPaginationControl = !itemCountWithinLimit && children && !disableAutomaticBottomPagination;
  const label = resourceLabel || PaginationResourceLabels[resourceKind];

  const calculateNewOffset = (direction: PaginationClick) => {
    if (direction === PaginationClick.FIRST) {
      return 0;
    } else if (direction === PaginationClick.PREV) {
      return prevPageOffset || 0;
    } else if (direction === PaginationClick.NEXT) {
      return nextPageOffset || 0;
    } else if (direction === PaginationClick.LAST) {
      return lastPageOffset || 0;
    }
    return 0;
  };

  return (
    <>
      {!disableAutomaticTopPagination && (
        <>
          {itemCountWithinLimit ? (
            <CounterHeader
              className={cx(styles.counterHeader, className)}
              resource={label}
              resourceCount={totalCount}
            />
          ) : (
            <PaginationControl
              offset={offset}
              currentPageSize={currentPageSize}
              resourceKind={resourceKind}
              isReady={isReady}
              onPageClick={(direction: PaginationClick) => onPageClick(direction, calculateNewOffset(direction))}
              totalCount={totalCount}
              limit={limit}
              className={className}
              hasInfinitePagination={hasInfinitePagination}
              resourceLabel={resourceLabel}
            />
          )}
        </>
      )}
      {children}
      {shouldRenderBottomPaginationControl && (
        <PaginationControl
          offset={offset}
          currentPageSize={currentPageSize}
          resourceKind={resourceKind}
          isReady={isReady}
          onPageClick={(direction: PaginationClick) => onPageClick(direction, calculateNewOffset(direction))}
          totalCount={totalCount}
          limit={limit}
          className={className}
          hasInfinitePagination={hasInfinitePagination}
          resourceLabel={resourceLabel}
        />
      )}
    </>
  );
}
